import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, TextField, Typography } from '@mui/material';
import { signInWithCustomToken } from 'firebase/auth';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import api from '../utils/api';
import { AuthContext } from '../auth/AuthContext';
import { useAuth } from '../auth/useAuth';
import { useIdleLock } from '../auth/useIdleLock';
import { recordActivity, setScreenLocked } from '../auth/idleLock';
import { SignOutNoticeCode } from '../auth/signOutNotice';
import { OFFLINE_ENABLED } from '../offline/entities';
import { unsyncedOpCount } from '../offline/opQueue';
import { drainQueue } from '../offline/pushQueue';
import { withSyncLock } from '../offline/OfflineProvider';

const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

interface UnlockCandidate {
  id: string;
  name: string;
}

/** The part of GET /users/me (App.tsx's shared ['current-user-me'] read) this screen needs. */
interface SignedInUser {
  id?: string;
  name?: string;
}

const triesLeft = (left: number) => `Wrong PIN. ${left} ${left === 1 ? 'try' : 'tries'} left.`;

/** Pushes this session's queued offline changes; true when none are left. */
async function uploadQueuedChanges(): Promise<boolean> {
  if (!OFFLINE_ENABLED || (await unsyncedOpCount()) === 0) return true;
  // The provider's own lock: two pushes of one op at once could both apply on the server.
  await withSyncLock(async () => {
    await drainQueue();
  });
  return (await unsyncedOpCount()) === 0;
}

/**
 * The workstation lock screen (plan T5, D5, D6): an opaque overlay over the page, which keeps its
 * state underneath for whoever unlocks as themselves. Anyone on the team unlocks with their name
 * and PIN; another person's unlock signs them in with the custom token the server mints and starts
 * them at the dashboard, so everything after it is theirs. Hidden while the 24-hour password dialog
 * is up (the password wins).
 */
const WorkstationLock: React.FC = () => {
  const { user, sessionExpiry } = useContext(AuthContext);
  const { signOutKeepingChanges } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { locked, online } = useIdleLock();
  const open =
    !DEV_BYPASS && locked && !!user && sessionExpiry !== 'locked' && (online || !OFFLINE_ENABLED);

  const [chosenId, setChosenId] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Everything cached was fetched as the person signed in then. Firebase shares the sign-in across
  // tabs, so a PIN swap in any tab, or someone else signing in after a log out, drops it here. A
  // swap (no log out between) also leaves this tab's page, as the unlocking tab does; after a log
  // out the login flow decides where the next person lands. A ref keeps the subscription stable.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  useEffect(() => {
    let lastUid = auth.currentUser?.uid ?? null;
    let signedOutSince = false;
    return auth.onAuthStateChanged((signedIn) => {
      if (!signedIn) {
        signedOutSince = true;
        return;
      }
      if (lastUid && signedIn.uid !== lastUid) {
        queryClient.clear();
        if (!signedOutSince) navigateRef.current('/', { replace: true });
      }
      lastUid = signedIn.uid;
      signedOutSince = false;
    });
  }, [queryClient]);

  useEffect(() => {
    if (!open) return;
    setChosenId(null);
    setPin('');
    setError('');
  }, [open]);

  const {
    data: candidates = [],
    isError: namesFailed,
    refetch: reloadNames,
  } = useQuery<UnlockCandidate[]>(
    ['unlock-candidates'],
    async () => (await api.get('/users/unlock-candidates')).data,
    { enabled: open && online, staleTime: 0 },
  );

  const me = queryClient.getQueryData<SignedInUser>(['current-user-me']);
  const myName = me?.name || user?.displayName || user?.email || 'The signed-in person';
  // Most unlocks are the person already signed in, so their name starts picked.
  const selectedId = chosenId ?? (candidates.some((c) => c.id === me?.id) ? me?.id ?? '' : '');

  // Whoever is here may not be the person signed in, so leaving never asks about, or discards, that
  // person's changes: they go up while the session still works and anything left stays for them.
  // A failed upload must not keep the computer signed in.
  const leave = async (notice?: SignOutNoticeCode) => {
    await uploadQueuedChanges().catch(() => false);
    await signOutKeepingChanges(notice);
  };

  const logOut = async () => {
    setBusy(true);
    try {
      await leave();
    } catch {
      /* signing out is local: nothing to show on this screen */
    } finally {
      setBusy(false);
    }
  };

  const unlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !selectedId || !/^\d{6}$/.test(pin)) return;
    setBusy(true);
    setError('');
    try {
      // Another person's unlock signs them in here, so this person's queued changes go up first.
      if (selectedId !== me?.id && !(await uploadQueuedChanges())) {
        setError(
          `Changes from ${myName} haven't uploaded yet. ${myName} needs to unlock to finish them, or log out.`,
        );
        return;
      }
      const { data } = await api.post('/users/unlock', { userId: selectedId, pin });
      if (data?.swap) {
        queryClient.clear();
        await signInWithCustomToken(auth, data.customToken);
        // Not the last person's page: a draft left on it would be saved under this person's name.
        navigate('/', { replace: true });
      }
      recordActivity(Date.now());
      setScreenLocked(false);
    } catch (err: any) {
      const body = err?.response?.data;
      const left = body?.details?.attemptsLeft;
      if (
        body?.code === 'UNLOCK_ATTEMPTS_EXHAUSTED' ||
        (body?.code === 'PIN_INCORRECT' && typeof left === 'number' && left <= 0)
      ) {
        await leave('UNLOCK_ATTEMPTS_EXHAUSTED');
        return;
      }
      setPin('');
      if (body?.code === 'PIN_INCORRECT') {
        setError(typeof left === 'number' ? triesLeft(left) : 'Wrong PIN.');
      } else if (err?.response?.status === 404) {
        setError("That name can't unlock this screen. Choose another name.");
        void reloadNames();
      } else if (err?.response?.status === 429) {
        setError('Too many tries at once. Wait a minute and try again.');
      } else {
        setError("Couldn't unlock. Check your connection and try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullScreen
      disableEscapeKeyDown
      aria-labelledby="workstation-lock-title"
      // Above anything the page can open, so nothing on the locked page shows through.
      sx={{ zIndex: (theme) => theme.zIndex.snackbar + 1 }}
      PaperProps={{ sx: { bgcolor: 'background.default', alignItems: 'center' } }}
    >
      <Box
        component="form"
        onSubmit={unlock}
        sx={{ width: '100%', maxWidth: 360, px: 2, my: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box component="img" src="/stabletrack-icon.png" alt="" sx={{ height: 56, width: 56, alignSelf: 'center' }} />
        <Typography id="workstation-lock-title" variant="h5" component="h1" textAlign="center">
          Screen locked
        </Typography>
        {online ? (
          <>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Choose your name and enter your PIN.
            </Typography>
            <TextField
              id="workstation-lock-name"
              select
              label="Name"
              value={selectedId}
              onChange={(e) => setChosenId(e.target.value)}
              // Native: an MUI menu would open beneath this overlay.
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              disabled={busy}
              fullWidth
            >
              <option value="" disabled>
                Choose your name
              </option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </TextField>
            <TextField
              id="workstation-lock-pin"
              label="PIN"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'off' }}
              disabled={busy}
              autoFocus
              fullWidth
            />
            {namesFailed && (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={() => void reloadNames()}>
                    Try again
                  </Button>
                }
              >
                Couldn't load the names.
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            <Button type="submit" variant="contained" disabled={busy || !selectedId || pin.length !== 6}>
              Unlock
            </Button>
          </>
        ) : (
          <Alert severity="info">You're offline. Reconnect to unlock, or log out.</Alert>
        )}
        <Button color="inherit" onClick={() => void logOut()} disabled={busy}>
          Log out
        </Button>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Forgot your PIN? Log out and sign in with your password.
        </Typography>
      </Box>
    </Dialog>
  );
};

export default WorkstationLock;
